import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Award, ArrowRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { careers } from '../mockData';

export const Careers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(careers.map(c => c.category))];

  const filteredCareers = careers.filter(career => {
    const matchesSearch = career.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         career.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || career.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-red-50 py-16 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Carreras Técnicas</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Descubre nuestra oferta de programas técnicos profesionales con alta demanda laboral y título a nombre de la nación
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 px-4 bg-white border-b">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Buscar carrera..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? 'bg-mit-blue hover:bg-blue-800' : ''}
                >
                  {category === 'all' ? 'Todas' : category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Careers Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          {filteredCareers.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">No se encontraron carreras que coincidan con tu búsqueda</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCareers.map((career, index) => (
                <Card key={career.id} className={`hover-lift overflow-hidden animate-fade-in-up animate-delay-${(index % 3) * 100}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={career.image}
                      alt={career.name}
                      className="w-full h-full object-cover hover-scale"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-mit-red text-white text-sm font-semibold rounded-full">
                        {career.category}
                      </span>
                    </div>
                    {career.convalidtionAvailable && (
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                          Homologable
                        </span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{career.name}</h3>
                    {career.shortDescription && (
                      <p className="text-gray-600 mb-4 line-clamp-2">{career.shortDescription}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{career.duration}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Award className="w-4 h-4" />
                        <span>{career.modality}</span>
                      </div>
                    </div>
                    <Link to={`/carreras/${career.id}`}>
                      <Button variant="outline" className="w-full border-mit-blue text-mit-blue hover:bg-mit-blue hover:text-white">
                        Ver Detalles
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <Card className="bg-mit-blue text-white border-0">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-white">¿No encuentras lo que buscas?</h2>
              <p className="text-xl mb-8 text-white/90">
                Contáctanos y te ayudaremos a encontrar la carrera ideal para ti
              </p>
              <Link to="/contacto">
                <Button size="lg" className="bg-white text-mit-blue hover:bg-gray-100 hover:text-mit-blue">
                  Contactar Asesor
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};